/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Injectable } from '@angular/core';
import { LmapService, ApiService, DialogService } from '@nrwl/common-lib';
import { BaseSetupService } from '@nrwl/test-setup';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class TwampResultsService extends BaseSetupService {
  lmapService: LmapService;
  isMosCodecEnabled = false;
  daysToReport = 1;
  maxFlowsInReport = 5;
  maxNRTFlows = 4;
  statsInterval = null;
  flowsCount = null;
  flowConfig = [];
  selectedFlows = [];
  selectedFlowIds = [];
  testPointName = null;
  NEGPORT_PENDING_TEXT = 'obtaining';
  selectedCodecSLA = null;
  codecAnalysis = null;
  scheduleId = null;
  testConfigurations = [];
  statsTypeOptions = [
    { name: '1-way', value: '1-way' },
    { name: '2-way', value: '2-way' }
  ];
  slaTypes = [
    {
      sla: 'FLR',
      label: 'Frame Loss Ratio (FLR)',
      defaultSla: 50
    },
    {
      sla: 'FD',
      label: 'Frame Delay (FD)',
      defaultSla: 200
    },
    {
      sla: 'FDV',
      label: 'Frame Delay Variation (FDV)',
      defaultSla: 50
    },
    {
      sla: 'QM',
      label: 'MOS',
      defaultSla: 3.2
    }
  ];

  codecTypeOptions = [
    { name: 'MOS', value: 'mos', sla: 3.2 },
    { name: 'R-factor', value: 'r-factor', sla: 62 }
  ];

  failedFlowStats = {
    FLR: {
      all: [],
      priority: {},
      endpoint: {},
      endpointAll: [],
      endpoint50More: [],
      endpoint50Less: []
    },
    FD: {
      all: [],
      priority: {},
      endpoint: {},
      endpointAll: [],
      endpoint50More: [],
      endpoint50Less: []
    },
    FDV: {
      all: [],
      priority: {},
      endpoint: {},
      endpointAll: [],
      endpoint50More: [],
      endpoint50Less: []
    },
    QM: {
      all: [],
      priority: {},
      endpoint: {},
      endpointAll: [],
      endpoint50More: [],
      endpoint50Less: []
    }
  };

  nrtSeriesList = [
    {
      name: '2-way',
      checked: true,
      class: 'twoway-btn'
    },
    {
      name: '1-way NF',
      checked: true,
      class: 'NF-btn'
    },
    {
      name: '1-way FN',
      checked: true,
      class: 'FN-btn'
    },
    {
      name: 'SLA',
      checked: false,
      class: 'sla-btn'
    }
  ];

  ///
  /// helper function to convert decimal DSCP value to human representation
  ///
  dscpString = {
    0: '0    (default)',
    8: 'CS1  (8  0x08)',
    10: 'AF11 (10 0x0A)',
    12: 'AF12 (12 0x0C)',
    14: 'AF13 (14 0x0E)',
    16: 'CS2  (16 0x10)',
    18: 'AF21 (18 0x12)',
    20: 'AF22 (20 0x14)',
    22: 'AF23 (22 0x16)',
    24: 'CS3  (24 0x18)',
    26: 'AF31 (26 0x1A)',
    28: 'AF32 (28 0x1C)',
    30: 'AF33 (30 0x1E)',
    32: 'CS4  (32 0x20)',
    34: 'AF41 (34 0x22)',
    36: 'AF42 (36 0x24)',
    38: 'AF43 (38 0x26)',
    40: 'CS5  (40 0x28)',
    42: 'AF51 (42 0x2A)',
    44: 'AF52 (44 0x2C)',
    46: 'EF   (46 0x2E)',
    48: 'CS6  (48 0x30)',
    56: 'CS7  (56 0x38)'
  };
  flowY1731Config: any[];
  sessionType: string;
  testName: string;

  constructor(
    apiService: ApiService,
    dialogService: DialogService,
    dialog: MatDialog,
    lmapService: LmapService
  ) {
    super(apiService, dialogService, dialog);
    this.lmapService = lmapService;
  }

  setScheuleId(id: number): void {
    this.scheduleId = id;
  }

  getTestName(): string {
    return this.testName;
  }

  // eslint-disable-next-line
  processTestParams(testConfig: any): void {
    let lmapConfig = null;
    this.testConfigurations = [];
    this.flowConfig = [];
    if (testConfig) {
      const primaryAgentInfo = this.lmapService.getAgentInfo('primary', testConfig);
      if (primaryAgentInfo) {
        this.testPointName = primaryAgentInfo.id;
        lmapConfig = this.lmapService.getMATestConfigByName(testConfig, primaryAgentInfo.config);

        if (lmapConfig) {
          const testSchedule = this.lmapService.extractScheduleByTaskName(
            lmapConfig,
            'twamp-initiator'
          );
          this.testName = testSchedule.name;
          this.testConfigurations.push(
            {
              fieldName: 'Test Name',
              value: this.testName
            },
            {
              fieldName: 'Id',
              value: this.scheduleId
            }
          );
          const pmResultSchedule = this.lmapService.extractReportScheduleByType(lmapConfig, 'pm');
          if (pmResultSchedule && pmResultSchedule.start) {
            const pmIntObj = this.lmapService.extractEventByName(
              lmapConfig,
              pmResultSchedule.start
            );
            if (pmIntObj && pmIntObj.periodic && typeof pmIntObj.periodic.interval === 'number') {
              this.statsInterval = parseInt(pmIntObj.periodic.interval) / 60; //convert s to min
              this.testConfigurations.push({
                fieldName: 'PM Interval',
                value: this.statsInterval + ' Minute'
              });
            }
          }
          let rtInterval = null;
          const rtResultSchedule = this.lmapService.extractReportScheduleByType(lmapConfig, 'rt');

          if (rtResultSchedule && rtResultSchedule.start) {
            const rtIntObj = this.lmapService.extractEventByName(
              lmapConfig,
              rtResultSchedule.start
            );
            if (rtIntObj && rtIntObj.periodic && typeof rtIntObj.periodic.interval === 'number') {
              rtInterval = parseInt(rtIntObj.periodic.interval);
              this.testConfigurations.push({
                fieldName: 'Realtime Interval',
                value: rtInterval
              });
            }
          }
          if (testSchedule) {
            // Source Info
            // get the source info from the first flow
            // since we don't know if we have TWAMP Full flows, Light Flows or both, check for both

            // First check for TWAMP Full flows
            // get first twamp-session-request within first twamp-client-ctrl-connection within twamp-client
            let clientConnectionArray = null;

            let srcIp = null;
            let startPort = null;

            const codecAnalysis = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'codec'
            );
            if (codecAnalysis && codecAnalysis !== 'none') {
              this.isMosCodecEnabled = true;
              this.selectedCodecSLA = codecAnalysis;
              this.codecAnalysis = codecAnalysis;
              this.testConfigurations.push({
                fieldName: 'MOS Codec Analysis',
                value: codecAnalysis
              });
            }

            let resultServer = null;
            const channel = this.lmapService.extractScheduleOptionByName(
              pmResultSchedule,
              'channel'
            );
            if (typeof channel === 'string') {
              const startIdx = channel.indexOf('//');
              if (startIdx >= 0) {
                const endIdx = channel.indexOf('/', startIdx + 2);
                if (endIdx > 0) {
                  resultServer = channel.substring(startIdx + 2, endIdx);
                }
              }
            }

            if (resultServer) {
              this.testConfigurations.push({
                fieldName: 'Results Server',
                value: resultServer
              });
            }

            let fullOptions = this.lmapService.extractScheduleOptionByName(testSchedule, 'twamp');
            if (fullOptions) {
              fullOptions = JSON.parse(fullOptions);
              if (
                fullOptions['ietf-twamp:twamp'] &&
                fullOptions['ietf-twamp:twamp']['twamp-client'] &&
                fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection']
              ) {
                clientConnectionArray =
                  fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection'];
                if (clientConnectionArray && clientConnectionArray.length > 0) {
                  if (
                    clientConnectionArray[0]['twamp-session-request'] &&
                    clientConnectionArray[0]['twamp-session-request'].length > 0
                  ) {
                    srcIp = clientConnectionArray[0]['twamp-session-request'][0]['sender-ip'];
                    startPort =
                      clientConnectionArray[0]['twamp-session-request'][0]['sender-udp-port'];
                  }
                }
              }
            }

            // Now check for TWAMP Light flows
            let lightOptions = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'twamp-light'
            );
            let map = null;
            let lightFlowArray = null;
            if (lightOptions) {
              map = this.lmapService.extractScheduleOptionByName(testSchedule, 'session-map');
              if (map) {
                map = JSON.parse(map);
              }
              lightOptions = JSON.parse(lightOptions);

              if (
                lightOptions['ietf-twamp-light:twamp-light'] &&
                lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'] &&
                lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
                  'test-session'
                ] &&
                lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
                  'test-session'
                ].length
              ) {
                lightFlowArray =
                  lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
                    'test-session'
                  ];
              }
            }

            if (srcIp === null && startPort === null) {
              if (lightFlowArray && lightFlowArray.length && lightFlowArray.length > 0) {
                if (lightFlowArray[0]['sender-ip']) {
                  srcIp = lightFlowArray[0]['sender-ip'];
                }
                if (lightFlowArray[0]['sender-udp-port']) {
                  startPort = lightFlowArray[0]['sender-udp-port'];
                }
              }
            }

            if (srcIp) {
              this.testConfigurations.push({
                fieldName: 'IP Address',
                value: srcIp
              });
            }

            const subnetPrefixLength = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'prefix-length'
            );
            if (subnetPrefixLength) {
              this.testConfigurations.push({
                fieldName: 'Subnet Prefix Length',
                value: subnetPrefixLength
              });
            } else {
              const subnet =
                this.lmapService.extractScheduleOptionByName(testSchedule, 'subnet') || '---';
              this.testConfigurations.push({
                fieldName: 'Subnet Mask',
                value: subnet
              });
            }

            let gateway = this.lmapService.extractScheduleOptionByName(testSchedule, 'gateway');
            if (!gateway) gateway = '---';
            this.testConfigurations.push({
              fieldName: 'Gateway',
              value: gateway
            });

            if (!startPort) startPort = '---';
            this.testConfigurations.push({
              fieldName: 'Base UDP Port',
              value: startPort
            });

            const vlanID = this.lmapService.extractScheduleOptionByName(testSchedule, 'vlan-id');
            const cVlanID = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'inner-vlan-id'
            );
            let vlanNumber = 'Untagged';
            let etherType = '';
            if (vlanID && cVlanID) {
              vlanNumber = 'Q-In-Q';
              etherType = this.lmapService.extractScheduleOptionByName(testSchedule, 'tpid');
              this.testConfigurations.push({
                fieldName: 'VLAN Configuration',
                value: vlanNumber
              });
            }
            if (etherType) {
              this.testConfigurations.push({
                fieldName: 'EtherType',
                value: etherType
              });
            }
            if (vlanID) {
              const vlanLabel = vlanNumber === 'Q-In-Q' ? 'S-VLAN' : 'VLAN';
              const sVlanPriority =
                this.lmapService.extractScheduleOptionByName(testSchedule, 'vlan-priority') || '0';
              this.testConfigurations.push(
                {
                  fieldName: vlanLabel + ' ID',
                  value: vlanID
                },
                {
                  fieldName: vlanLabel + ' Priority',
                  value: 'Priority ' + sVlanPriority
                }
              );
            }
            if (cVlanID) {
              const cVlanPriority =
                this.lmapService.extractScheduleOptionByName(testSchedule, 'inner-vlan-priority') ||
                '0';
              this.testConfigurations.push(
                {
                  fieldName: 'C-VLAN ID',
                  value: cVlanID
                },
                {
                  fieldName: 'C-VLAN Priority',
                  value: 'Priority ' + cVlanPriority
                }
              );
            }

            //
            // build a list of all the flows defined in this test
            // these will be used to populate things like the NRT list
            // maybe even build a lookup list to speed up 5 second NRT processing
            //
            if (fullOptions && clientConnectionArray) {
              this.processConfigFullFlows(srcIp, fullOptions, clientConnectionArray);
            }

            if (lightFlowArray) {
              this.processConfigLightFlows(srcIp, lightFlowArray, map);
            }
          }
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  processConfigFullFlows(srcIp, fullOptions, clientConnectionArray): void {
    // get twamp-client-ctrl-connection array
    if (clientConnectionArray) {
      // get twamp-sender-test-session array
      let senderTestSessionArray = null;
      if (
        fullOptions &&
        fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'] &&
        fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
          'twamp-sender-test-session'
        ] &&
        fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
          'twamp-sender-test-session'
        ].length > 0
      ) {
        senderTestSessionArray =
          fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
            'twamp-sender-test-session'
          ];
      }

      const negPort = this.NEGPORT_PENDING_TEXT;
      for (const x in clientConnectionArray) {
        if (
          clientConnectionArray[x]['twamp-session-request'] &&
          clientConnectionArray[x]['twamp-session-request'].length > 0
        ) {
          for (const y in clientConnectionArray[x]['twamp-session-request']) {
            const fid = clientConnectionArray[x]['twamp-session-request'][y]['test-session-name'];
            let destIP = clientConnectionArray[x]['twamp-session-request'][y]['reflector-ip'];
            if (!destIP) destIP = '---';

            let dscp = clientConnectionArray[x]['twamp-session-request'][y]['dscp'];
            if (!dscp && isNaN(dscp)) {
              dscp = '---';
            } else {
              if (this.dscpString[dscp]) dscp = this.dscpString[dscp];
            }

            let port = clientConnectionArray[x]['twamp-session-request'][y]['reflector-udp-port'];
            if (!port) port = '---';

            let frameSize = clientConnectionArray[x]['twamp-session-request'][y]['padding-length'];
            if (typeof frameSize !== 'undefined') {
              const padLen = parseInt(frameSize);
              if (!isNaN(padLen)) {
                frameSize = padLen + 87;
              } else {
                frameSize = '---';
              }
            } else {
              frameSize = '---';
            }

            let txPeriod = null;
            for (const z in senderTestSessionArray) {
              if (senderTestSessionArray[z]['test-session-name'] === fid) {
                txPeriod = senderTestSessionArray[z]['periodic-interval'];
                break;
              }
            }
            if (!txPeriod) txPeriod = '---';

            let controlServerIp = null;
            let controlServerPort = null;

            if (clientConnectionArray[x]['server-ip']) {
              controlServerIp = clientConnectionArray[x]['server-ip'];
            }
            if (!controlServerIp) controlServerIp = '';

            if (clientConnectionArray[x]['server-tcp-port']) {
              controlServerPort = clientConnectionArray[x]['server-tcp-port'];
            }
            if (!controlServerPort) controlServerPort = '';

            const sessionType = 'TWAMP';
            this.addFlowToConfig(
              fid,
              srcIp,
              destIP,
              port,
              negPort,
              dscp,
              txPeriod,
              frameSize,
              sessionType,
              controlServerIp,
              controlServerPort
            );
          }
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  processConfigLightFlows(srcIp, lightFlowArray, map): void {
    if (lightFlowArray) {
      for (const z in lightFlowArray) {
        let fid = lightFlowArray[z]['session-id'];
        let sessionType = 'TWAMP Light';

        // look name up in map if provided
        if (map) {
          for (const entry in map) {
            if (entry === fid.toString() && map[entry].name) {
              fid = map[entry].name;
              sessionType = map[entry]['payload-type'] === 1 ? 'Ping' : 'TWAMP Light';
              break;
            }
          }
        }

        let destIP = lightFlowArray[z]['reflector-ip'];
        if (!destIP) destIP = '---';

        let dscp = lightFlowArray[z]['dscp'];
        if (!dscp && isNaN(dscp)) {
          dscp = '---';
        } else {
          if (this.dscpString[dscp]) dscp = this.dscpString[dscp];
        }

        let port = sessionType === 'Ping' ? '-' : lightFlowArray[z]['reflector-udp-port'];
        if (!port) port = '---';

        const negPort = port; // no negotiation happens, configured port is what test uses

        let frameSize = lightFlowArray[z]['packet-padding-size'];
        if (typeof frameSize !== 'undefined') {
          const padLen = parseInt(frameSize);
          if (!isNaN(padLen)) {
            frameSize = padLen + 87;
          } else {
            frameSize = '---';
          }
        } else {
          frameSize = '---';
        }

        let txPeriod = lightFlowArray[z]['interval'];
        if (!txPeriod) txPeriod = '---';

        const controlServerIp = '';
        const controlServerPort = '';
        this.addFlowToConfig(
          fid,
          srcIp,
          destIP,
          port,
          negPort,
          dscp,
          txPeriod,
          frameSize,
          sessionType,
          controlServerIp,
          controlServerPort
        );
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  addFlowToConfig(
    fid,
    srcIp,
    destIP,
    port,
    negPort,
    dscp,
    txPeriod,
    frameSize,
    sessionType,
    controlServerIp,
    controlServerPort
  ): void {
    // use flow name as the key and make a flow lookup table
    // so we can show the IPAddr, PRI and port in tables and graphs
    if (fid) {
      this.flowConfig[fid] = {
        srcIp: { label: 'Source IP', value: srcIp, units: '' },
        ip: { label: 'Dest IP', value: destIP, units: '' },
        port: { label: 'Requested Dest Port', value: port, units: '' },
        negPort: { label: 'Dest Port', value: negPort, units: '' },
        dscp: { label: 'Service / DSCP', value: dscp, units: '' },
        txPeriod: { label: 'TX Period', value: txPeriod, units: 'ms' },
        frameSize: { label: 'Frame Size', value: frameSize, units: '' },
        sessionType: { label: 'Session Type', value: sessionType, units: '' },
        controlServerIp: { label: 'Control Server IP', value: controlServerIp, units: '' },
        controlServerPort: { label: 'Control Server Port', value: controlServerPort, units: '' }
      };
    }
  }

  getStatsInterval(): number {
    return this.statsInterval;
  }

  getTestpointName(): string {
    return this.testPointName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setSelectedFlowsforNrtResult(flows: any): void {
    this.selectedFlows = flows;
  }

  getSelectedFlows(): any {
    return this.selectedFlows;
  }

  getSelectedFlowIds(): any {
    const flowIds = this.selectedFlowIds.map(data => data.flowId);
    return flowIds;
  }

  getSLAforCodecType(codec: string): number {
    const obj = this.codecTypeOptions.find(o => o.value === codec);
    return obj.sla;
  }

  setFlowConfig(config: any): void {
    this.flowY1731Config = config;
  }

  getFlowConfig(): any {
    return this.flowY1731Config;
  }

  getDefaultSlaValues(slaType: string): number {
    const obj = this.slaTypes.find(o => o.sla === slaType);
    return obj.defaultSla;
  }

  getFlowDetailsButton(flowId: string): any {
    const flowDetails = [];
    for (const key in this.flowConfig[flowId]) {
      if (key !== 'negPort') {
        // no need to display negotiated port with user defined info
        if (
          this.flowConfig?.[flowId]?.[key]?.value !== '' &&
          typeof this.flowConfig[flowId][key].value !== 'undefined'
        ) {
          flowDetails.push({
            fieldName: this.flowConfig[flowId][key]?.label,
            value: this.flowConfig[flowId][key]?.value + this.flowConfig[flowId][key]?.units
          });
        }
      }
    }
    return flowDetails;
  }

  isMosEnabled(): boolean {
    return this.isMosCodecEnabled;
  }
}
